import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRoutesList } from '../../api/routes/routesApi';
import { Route, Organization, ProviderType, DestinationType } from '../common/ObjectTypes';
import RoutesList from './RoutesList';
import { loadAllOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from '../../api/apiUtils';
import { useKeycloakToken } from '../common/useKeycloak';
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import { loadProviderTypes } from '../../api/routes/loadProviderTypesFilter';
import getUserPermissions from '../../api/users/getUserPermissions';
import { useKeycloak } from '@react-keycloak/web';
import { useHelp } from '../../context/HelpContext';
import { useTranslation } from 'react-i18next';
import { hideReverseRoutes } from "./hideReverseRoutes";

const RoutesPage = () => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();

    useEffect(() => {
        setHelpContent({
            cards: [{
                title: t('Managing Routes in Gundi'),
                description: t('Learn how to create and manage Routes between data sources and destinations.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/en_US/routes-gundi/routes' },
            }],
        });
    }, [i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    type FilterTypes = {
        search: string,
        provider_type__in: FilterTags[],
        destination_type__in: FilterTags[],
        destination_url__in: FilterTags[],
        owner__in: FilterTags[],
        status: FilterTags[],
        ordering: string,
    }
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultRoutesFilter = {
        provider_type__in: [],
        destination_type__in: [],
        destination_url__in: [],
        owner__in: [],
        status: [],
        search: "",
        ordering: "name"
    };
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const [canAddRoute, setCanAddRoute] = useState(false);
    const [routes, setRoutes] = useState<Route[]>([]);
    const [routesFilter, setRoutesFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('routesFilter');
        return savedFilter ? { ...defaultRoutesFilter, ...JSON.parse(savedFilter) } : defaultRoutesFilter;
    });
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [loadingProviderTypes, setLoadingProviderTypes] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [reloadRoutes, setReloadRoutes] = useState(true);
    const navigate = useNavigate();

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                setOrganizations(await loadAllOrganizations(token));
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let urlsOffset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, urlsOffset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (urlsOffset + filter_page_size >= total) break;
                    urlsOffset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadProviderTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingProviderTypes(true);
            try {
                let accumulated: ProviderType[] = [];
                let offset = 0;
                while (true) {
                    const [types, total, error] = await loadProviderTypes(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setProviderTypes(accumulated);
            } finally {
                setLoadingProviderTypes(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let offset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    useEffect(() => {
        sessionStorage.setItem('routesFilter', JSON.stringify(routesFilter));
        const loadRoutes = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const routeFilterQuery = {
                        provider_type__in: routesFilter.provider_type__in.map(a => a.id).toString(),
                        destination_type__in: routesFilter.destination_type__in.map(a => a.id).toString(),
                        destination_url__in: routesFilter.destination_url__in.map(a => (a.id)).toString(),
                        owner__in: routesFilter.owner__in.map(a => a.id).toString(),
                        status: routesFilter.status.map(a => a.id).toString(),
                        ordering: routesFilter.ordering,
                        search: routesFilter.search,
                        offset: offset,
                        previous: process.env.REACT_APP_BASE_URL_LOCAL + "/routes/?pager=limit&limit=" + page_size + "&offset=" + (offset - page_size),
                        next: process.env.REACT_APP_BASE_URL_LOCAL + "/routes/?pager=limit&limit=" + page_size + "&offset=" + (offset + page_size)
                    }
                    const temp = await getRoutesList(token, routeFilterQuery)
                    // One row per flow: the reverse Route of a bidirectional flow is
                    // already an arrow on its partner's map. GUNDI-5680 temporary,
                    // client-side and same-page only (count stays the server's) —
                    // see the reviewer note in hideReverseRoutes.ts; GUNDI-5681 retires it.
                    setRoutes(hideReverseRoutes(temp.results))
                    setTotalCount(temp.count);
                } catch (err) {
                    console.log(err)
                } finally {
                    // Always settle the flag — a failed request must not leave
                    // the refresh button disabled and spinning forever.
                    setReloadRoutes(false);
                    setPageLoaded(true);
                }
            }
        }
        if (reloadRoutes) loadRoutes();
    }, [token, reloadRoutes, routesFilter]);

    useEffect(() => {
        if (!pageLoaded) return;
        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddRoute(temp);
                } catch (err) {
                    console.log(err)
                }
            }
        }
        loadPerms();
        loadOrganizationsData();
        loadDestinationURlsData();
        loadProviderTypesData();
        loadDestinationTypesData();
    }, [pageLoaded]);

    const handleGlobalSearch = (searchSubStr: string) => {
        setRoutesFilter((prevFilter: any) => ({
            ...prevFilter,
            search: searchSubStr
        }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleFilterByStatus = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setRoutesFilter(prev => ({ ...prev, status: tags }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleFilterDestinationUrl = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setRoutesFilter(prev => ({ ...prev, destination_url__in: tags }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setRoutesFilter(prev => ({ ...prev, owner__in: tags }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleFilterProviderType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setRoutesFilter(prev => ({ ...prev, provider_type__in: tags }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleFilterDestinationType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setRoutesFilter(prev => ({ ...prev, destination_type__in: tags }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleSortBy = (selected: { label: string, value: string }) => {
        setRoutesFilter(prev => ({ ...prev, ordering: selected.value }));
        setOffset(0);
        setReloadRoutes(true);
    }

    const handleSelectRoute = (route_id: string, subpage?: string) => {
        navigate("/routes/" + route_id + (subpage ? "/" + subpage : ""))
    }

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setReloadRoutes(true);
    }

    const handleClickAdd = () => {
        navigate("/routes/new")
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const statusSelectValue = toSingleValue(routesFilter.status);
    const destinationSelectValue = toSingleValue(routesFilter.destination_url__in);
    const ownerSelectValue = toSingleValue(routesFilter.owner__in);
    const providerTypeSelectValue = toSingleValue(routesFilter.provider_type__in);
    const destinationTypeSelectValue = toSingleValue(routesFilter.destination_type__in);

    return (
            <div>
                <RoutesList
                    canAddRoute={canAddRoute}
                    routes={routes}
                    reloadRoutes={reloadRoutes}
                    organizations={organizations}
                    destinationUrls={destinationUrls}
                    providerTypes={providerTypes}
                    loadingProviderTypes={loadingProviderTypes}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    onSelectRoute={handleSelectRoute}
                    onChangeGlobalSearch={handleGlobalSearch}
                    onRefresh={() => setReloadRoutes(true)}
                    onChangeStatusFilter={handleFilterByStatus}
                    statusFilterValue={statusSelectValue}
                    onChangeDestinationUrl={handleFilterDestinationUrl}
                    destinationFilterValue={destinationSelectValue}
                    loadingDestinationUrls={loadingDestinationUrls}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    loadingOrganizations={loadingOrganizations}
                    onChangeProviderType={handleFilterProviderType}
                    providerTypeFilterValue={providerTypeSelectValue}
                    onChangeDestinationType={handleFilterDestinationType}
                    destinationTypeFilterValue={destinationTypeSelectValue}
                    globalSearchStr={routesFilter.search}
                    ordering={routesFilter.ordering}
                    onChangeSortBy={handleSortBy}
                    onPageClicked={handlePageClick}
                    totalCount={totalCount}
                    pageLoaded={pageLoaded}
                    take={page_size}
                    offset={offset}
                    onClickAdd={handleClickAdd}
                />
            </div>
        );
};

export default RoutesPage;
